import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Calendar, 
  ChevronRight, 
  Clock, 
  Code, 
  Database, 
  DollarSign, 
  Gift, 
  GraduationCap, 
  HardDrive, 
  Heart, 
  Laptop, 
  LineChart, 
  
  Monitor, 
 
  Star, 
  TrendingUp, 
  Users, 
  Check,
  ArrowRight,
  User,
  Briefcase,
  Filter,
  Layers
} from 'lucide-react';
import { Tab, Tabs, Table, Container, Row, Col, Card, Button, Badge, Accordion } from 'react-bootstrap';
import { FaLaptopCode, FaUserTie } from "react-icons/fa";

// Course data
const sapCourses = {
  foundational: [
    {
      id: 1,
      title: "SAP Fundamentals: A Complete Beginner's Guide",
      description: "Master the core concepts of SAP ERP systems and understand the foundation of enterprise solutions.",
      duration: 3,
      level: "Beginner",
      icon: <BookOpen />,
      trending: false,
      futureRelevance: {
        2025: 4,
        2026: 3,
        2027: 3
      },
      curriculum: [
        "Introduction to ERP and SAP",
        "SAP Architecture & Navigation",
        "Core Business Processes",
        "SAP Modules Overview",
        "System Integration Basics"
      ],
      tools: ["SAP GUI", "SAP Fiori Launchpad", "SAP Business Client"],
      careerPaths: ["Junior SAP Consultant", "SAP Support Analyst", "Business Process Analyst"]
    },
    {
      id: 2,
      title: "SAP Essentials: Navigating the World of Enterprise Solutions",
      description: "Comprehensive introduction to SAP's enterprise ecosystem, providing a solid knowledge base for specialization.",
      duration: 3,
      level: "Beginner",
      icon: <Laptop />,
      trending: false,
      futureRelevance: {
        2025: 4,
        2026: 3,
        2027: 3
      },
      curriculum: [
        "Enterprise Business Processes",
        "SAP System Architecture",
        "Cross-Module Integration",
        "SAP Implementation Methodology",
        "SAP Support and Lifecycle"
      ],
      tools: ["SAP GUI", "SAP Solution Manager", "SAP Fiori"],
      careerPaths: ["SAP Business Analyst", "SAP Support Specialist", "Junior Implementation Consultant"]
    }
  ],
  functional: [
    {
      id: 3,
      title: "Mastering SAP FICO (Financial Accounting and Controlling)",
      description: "Master financial accounting and controlling in SAP to manage enterprise finance operations.",
      duration: 6,
      level: "Intermediate",
      icon: <DollarSign />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 4
      },
      curriculum: [
        "General Ledger Accounting",
        "Accounts Payable & Receivable",
        "Asset Accounting",
        "Cost Center Accounting",
        "Profit Center Accounting",
        "Reporting & Analytics"
      ],
      tools: ["SAP S/4HANA Finance", "SAP Fiori", "SAP Analytics Cloud"],
      careerPaths: ["SAP FICO Consultant", "Financial Systems Analyst", "SAP Finance Process Owner"]
    },
    {
      id: 4,
      title: "SAP MM (Material Management) Certification Course",
      description: "Comprehensive training on procurement, inventory management, and material valuation processes.",
      duration: 6,
      level: "Intermediate",
      icon: <Database />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 4,
        2027: 4
      },
      curriculum: [
        "Master Data in MM",
        "Procurement Process",
        "Inventory Management",
        "Material Valuation",
        "Physical Inventory",
        "Integration with other modules"
      ],
      tools: ["SAP S/4HANA MM", "SAP Ariba", "SAP Fiori"],
      careerPaths: ["SAP MM Consultant", "Procurement Specialist", "Supply Chain Analyst"]
    },
    {
      id: 5,
      title: "SAP SD (Sales and Distribution) for Beginners to Advanced",
      description: "Learn sales, billing, shipping and distribution processes using SAP's powerful SD module.",
      duration: 6,
      level: "Intermediate",
      icon: <TrendingUp />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 4,
        2027: 4
      },
      curriculum: [
        "Sales Master Data",
        "Sales Order Processing",
        "Shipping & Transportation",
        "Billing & Revenue Recognition",
        "Credit Management",
        "Sales Analytics & Reporting"
      ],
      tools: ["SAP S/4HANA Sales", "SAP CRM", "SAP Fiori"],
      careerPaths: ["SAP SD Consultant", "Sales Operations Specialist", "Order Management Specialist"]
    },
    {
      id: 6,
      title: "SAP HCM (Human Capital Management) Professional Training",
      description: "Comprehensive training on SAP's human resources management solutions.",
      duration: 6,
      level: "Intermediate",
      icon: <Users />,
      trending: false,
      futureRelevance: {
        2025: 3,
        2026: 3,
        2027: 2
      },
      curriculum: [
        "Personnel Administration",
        "Organizational Management",
        "Time Management",
        "Payroll Processing",
        "Travel Management",
        "ESS/MSS Implementation"
      ],
      tools: ["SAP HCM", "SAP SuccessFactors", "SAP Fiori"],
      careerPaths: ["SAP HCM Consultant", "HR Systems Analyst", "HRIS Specialist"]
    }
  ],
  technical: [
    {
      id: 7,
      title: "SAP ABAP Programming: From Basics to Advanced",
      description: "Master SAP's programming language to develop custom applications and enhance existing functionality.",
      duration: 6,
      level: "Intermediate to Advanced",
      icon: <Code />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 4
      },
      curriculum: [
        "ABAP Basics & Syntax",
        "Data Dictionary",
        "Reports & Forms",
        "Dialog Programming",
        "Object-Oriented ABAP",
        "SAP Fiori Integration",
        "Web Dynpro ABAP"
      ],
      tools: ["SAP NetWeaver AS ABAP", "Eclipse IDE", "ABAP Development Tools"],
      careerPaths: ["SAP ABAP Developer", "SAP Technical Consultant", "SAP Solutions Architect"]
    },
    {
      id: 8,
      title: "SAP BASIS Administration and Security",
      description: "Learn system administration, performance tuning, security, and authorizations management.",
      duration: 6,
      level: "Intermediate to Advanced",
      icon: <HardDrive />,
      trending: true,
      futureRelevance: {
        2025: 4,
        2026: 4,
        2027: 4
      },
      curriculum: [
        "System Installation & Configuration",
        "User Administration",
        "Transport Management",
        "System Monitoring",
        "Performance Tuning",
        "Security & Authorization",
        "Backup & Recovery"
      ],
      tools: ["SAP NetWeaver", "Solution Manager", "SAP HANA Cockpit"],
      careerPaths: ["SAP Basis Administrator", "SAP Security Consultant", "SAP System Architect"]
    }
  ],
  specialized: [
    {
      id: 9,
      title: "SAP S/4HANA Migration and Implementation Specialist Course",
      description: "Master the latest ERP suite from SAP with focus on implementation and migration strategies.",
      duration: 9,
      level: "Advanced",
      icon: <Layers />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "S/4HANA Architecture",
        "Migration Strategies",
        "Brownfield vs Greenfield",
        "Custom Code Migration",
        "Data Migration",
        "Fiori Implementation",
        "Post-Migration Operations"
      ],
      tools: ["SAP S/4HANA", "SAP Fiori", "SAP Solution Manager", "SAP Readiness Check"],
      careerPaths: ["S/4HANA Implementation Specialist", "SAP Solution Architect", "Digital Transformation Consultant"]
    },
    {
      id: 10,
      title: "SAP Fiori/UI5 App Development for Modern Enterprises",
      description: "Build modern, intuitive user interfaces for SAP applications using Fiori and UI5 frameworks.",
      duration: 6,
      level: "Intermediate to Advanced",
      icon: <Monitor />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "Fiori Design Principles",
        "SAPUI5 Framework",
        "MVC Architecture",
        "OData Services",
        "Fiori Elements",
        "Freestyle SAPUI5",
        "Extension & Customization"
      ],
      tools: ["SAPUI5", "SAP Web IDE", "SAP Business Application Studio", "SAP Fiori Tools"],
      careerPaths: ["SAP UI/UX Developer", "Fiori Consultant", "Frontend SAP Developer"]
    },
    {
      id: 11,
      title: "SAP Analytics Cloud (SAC) and Data Visualization Expert Program",
      description: "Master SAP's next-generation cloud analytics platform for business intelligence and planning.",
      duration: 6,
      level: "Intermediate to Advanced",
      icon: <LineChart />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "Data Modeling in SAC",
        "Story Building & Visualization",
        "Planning Applications",
        "Predictive Analytics",
        "Integration with SAP BW",
        "Data Connection Options",
        "Mobile Analytics"
      ],
      tools: ["SAP Analytics Cloud", "SAP HANA", "SAP Data Warehouse Cloud"],
      careerPaths: ["SAP Analytics Consultant", "BI Solution Architect", "Data Visualization Specialist"]
    },
    {
      id: 12,
      title: "SAP Ariba: Procurement and Supply Chain Management",
      description: "Master cloud-based procurement and supplier relationship management solutions.",
      duration: 6,
      level: "Intermediate to Advanced",
      icon: <Gift />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "Guided Buying",
        "Strategic Sourcing",
        "Supplier Management",
        "Contract Management",
        "Procurement Operations",
        "Integration with SAP ERP",
        "Spend Analysis"
      ],
      tools: ["SAP Ariba", "SAP S/4HANA", "SAP Fieldglass"],
      careerPaths: ["SAP Ariba Consultant", "Procurement Solutions Architect", "Digital Supply Chain Specialist"]
    },
    {
      id: 13,
      title: "SAP SuccessFactors (HR Cloud Solutions) Certification",
      description: "Implement and configure SAP's cloud-based human capital management solution.",
      duration: 6,
      level: "Intermediate to Advanced",
      icon: <Heart />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "Employee Central",
        "Recruitment Management",
        "Onboarding",
        "Learning Management",
        "Performance & Goals",
        "Compensation Management",
        "Workforce Analytics"
      ],
      tools: ["SAP SuccessFactors", "SAP HCM", "SAP Analytics Cloud"],
      careerPaths: ["SuccessFactors Consultant", "HR Technology Specialist", "Talent Management Consultant"]
    }
  ],
  careerPacks: [
    {
      id: 14,
      title: "SAP Consultant Career Path Program (Functional + Technical)",
      description: "Comprehensive program combining functional and technical skills for a complete SAP consultant profile.",
      duration: 9,
      level: "Beginner to Advanced",
      icon: <GraduationCap />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "SAP Fundamentals",
        "Core Functional Module (FICO, MM, or SD)",
        "ABAP Programming Essentials",
        "S/4HANA Fundamentals",
        "Fiori User Experience",
        "Project Implementation Methodology",
        "Certification Preparation"
      ],
      tools: ["SAP S/4HANA", "SAP Fiori", "ABAP Development Tools", "SAP Solution Manager"],
      careerPaths: ["SAP Consultant", "SAP Solution Architect", "SAP Project Manager"]
    },
    {
      id: 15,
      title: "Complete SAP Package: Learn ABAP, BASIS, and Fiori Together",
      description: "Become a versatile SAP technical professional with expertise in programming, administration, and UX.",
      duration: 9,
      level: "Intermediate to Advanced",
      icon: <Award />,
      trending: true,
      futureRelevance: {
        2025: 5,
        2026: 5,
        2027: 5
      },
      curriculum: [
        "ABAP Programming",
        "SAP BASIS Administration",
        "Security & Authorizations",
        "Fiori App Development",
        "System Integration",
        "Cloud Platform Extension",
        "DevOps for SAP"
      ],
      tools: ["SAP NetWeaver", "ABAP Development Tools", "SAP Fiori Tools", "SAP BTP"],
      careerPaths: ["SAP Technical Architect", "Full-Stack SAP Developer", "SAP DevOps Engineer"]
    }
  ]
};

// Calculate course future score (for 2025-2027)
const calculateFutureScore = (course) => {
  if (!course.futureRelevance) return 0;
  return (course.futureRelevance[2025] * 0.5) + 
         (course.futureRelevance[2026] * 0.3) + 
         (course.futureRelevance[2027] * 0.2);
};

// Get top recommended courses
const getTopRecommendedCourses = () => {
  const allCourses = [
    ...sapCourses.foundational,
    ...sapCourses.functional,
    ...sapCourses.technical,
    ...sapCourses.specialized,
    ...sapCourses.careerPacks
  ];
  
  return allCourses
    .sort((a, b) => calculateFutureScore(b) - calculateFutureScore(a))
    .slice(0, 6);
};

export default function SAPCourses() {
  const [filter, setFilter] = useState('all');
  const [duration, setDuration] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [topCourses, setTopCourses] = useState([]);

  useEffect(() => {
    setTopCourses(getTopRecommendedCourses());
  }, []);

  const filteredCourses = () => {
    let courses = [];
    
    // Add courses based on category filter
    if (filter === 'all' || filter === 'foundational') courses = [...courses, ...sapCourses.foundational];
    if (filter === 'all' || filter === 'functional') courses = [...courses, ...sapCourses.functional];
    if (filter === 'all' || filter === 'technical') courses = [...courses, ...sapCourses.technical];
    if (filter === 'all' || filter === 'specialized') courses = [...courses, ...sapCourses.specialized];
    if (filter === 'all' || filter === 'careerPacks') courses = [...courses, ...sapCourses.careerPacks];
    
    // Filter by duration
    if (duration !== 'all') {
      courses = courses.filter(course => course.duration === parseInt(duration));
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      courses = courses.filter(course => 
        course.title.toLowerCase().includes(term) || 
        course.description.toLowerCase().includes(term)
      );
    }
    
    return courses;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  const renderCourseCard = (course) => {
    
    return (
      <motion.div 
        key={course.id}
        variants={itemVariants}
        className="mb-4"
      >
        <Card className="h-100 shadow-sm hover:shadow-lg transition-shadow duration-300">
          <Card.Header className="bg-gray-100 d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center">
              <div className="text-blue-600 me-2">
                {course.icon}
              </div>
              <h5 className="mb-0 fw-bold">{course.title}</h5>
            </div>
            {course.trending && (
              <Badge bg="danger" className="ms-2">Trending</Badge>
            )}
          </Card.Header>
          <Card.Body>
            <div className="d-flex justify-content-between mb-3">
              <Badge bg="primary" className="d-flex align-items-center">
                <Clock size={14} className="me-1" />
                {course.duration} Months
              </Badge>
              <Badge bg="secondary" className="d-flex align-items-center">
                <User size={14} className="me-1" />
                {course.level}
              </Badge>
            </div>
            
            <Card.Text>{course.description}</Card.Text>
            
            <div className="mb-3">
              <p className="fw-bold mb-1 d-flex align-items-center">
                <Layers size={16} className="me-1 text-primary" />
                Future Relevance:
              </p>
              <div className="d-flex">
                <div className="me-2">
                  <small className="fw-bold">2025</small>
                  <div className="d-flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={14} 
                        className={i < (course.futureRelevance?.[2025] || 0) ? "text-warning" : "text-gray-300"} 
                        fill={i < (course.futureRelevance?.[2025] || 0) ? "currentColor" : "none"}
                      />
                    ))}
                  </div>
                </div>
                <div className="me-2">
                  <small className="fw-bold">2026</small>
                  <div className="d-flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={14} 
                        className={i < (course.futureRelevance?.[2026] || 0) ? "text-warning" : "text-gray-300"} 
                        fill={i < (course.futureRelevance?.[2026] || 0) ? "currentColor" : "none"}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <small className="fw-bold">2027</small>
                  <div className="d-flex">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        size={14} 
                        className={i < (course.futureRelevance?.[2027] || 0) ? "text-warning" : "text-gray-300"} 
                        fill={i < (course.futureRelevance?.[2027] || 0) ? "currentColor" : "none"}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <Accordion className="mb-3">
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    <BookOpen size={16} className="me-2 text-primary" />
                    Curriculum Overview
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <ul className="mb-0 ps-3">
                    {course.curriculum.map((item, index) => (
                      <li key={index} className="mb-1">
                        <div className="d-flex align-items-center">
                          <Check size={14} className="me-1 text-success" />
                          {item}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    <Laptop size={16} className="me-2 text-primary" />
                    Tools & Technologies
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <div className="d-flex flex-wrap gap-1">
                    {course.tools.map((tool, index) => (
                      <Badge key={index} bg="light" text="dark" className="me-1 mb-1">
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  <div className="d-flex align-items-center">
                    <Briefcase size={16} className="me-2 text-primary" />
                    Career Paths
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <ul className="mb-0 ps-3">
                    {course.careerPaths.map((path, index) => (
                      <li key={index}>
                        <div className="d-flex align-items-center">
                          <ArrowRight size={14} className="me-1 text-primary" />
                          {path}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Card.Body>
          <Card.Footer className="bg-white border-top-0">
            <div className="d-grid">
              <Button variant="primary" className="d-flex align-items-center justify-content-center">
                Enroll Now
                <ChevronRight size={16} className="ms-1" />
              </Button>
            </div>
          </Card.Footer>
        </Card>
      </motion.div>
    );
  };

  return (
    <Container fluid className="py-5 bg-light">
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-5"
      >
        <h1 className="display-4 fw-bold mb-3">SAP Training Courses</h1>
        <p className="lead mb-4">Future-proof your career with our industry-leading SAP certification courses</p>
      <Row className="mb-4">
  <Col xs={6} sm={6} md={6} lg={3} className="mb-2">
    <Badge bg="primary" className="w-100 p-2 d-flex align-items-center justify-content-center">
      <Award size={16} className="me-1" />
      Industry-Certified
    </Badge>
  </Col>
  <Col xs={6} sm={6} md={6} lg={3} className="mb-2">
    <Badge bg="success" className="w-100 p-2 d-flex align-items-center justify-content-center">
      <Users size={16} className="me-1" />
      Expert Instructors
    </Badge>
  </Col>
  <Col xs={6} sm={6} md={6} lg={3} className="mb-2">
    <Badge bg="info" className="w-100 p-2 d-flex align-items-center justify-content-center">
      <Laptop size={16} className="me-1" />
      Hands-on Labs
    </Badge>
  </Col>
  <Col xs={6} sm={6} md={6} lg={3} className="mb-2">
    <Badge bg="warning" text="dark" className="w-100 p-2 d-flex align-items-center justify-content-center">
      <Calendar size={16} className="me-1" />
      Flexible Schedules
    </Badge>
  </Col>
</Row>
      </motion.div>

      {/* Top Recommended Courses for 2025-2027 */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        className="mb-5"
      >
        <Container>
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h2 className="mb-0 fw-bold">
              <Star className="text-warning me-2" fill="currentColor" />
              Top SAP Courses for 2025-2027
            </h2>
            <Button variant="outline-primary" size="sm" className="d-flex align-items-center">
              View All Courses
              <ChevronRight size={16} className="ms-1" />
            </Button>
          </div>
          
          <Row>
            {topCourses.map(course => (
              <Col key={course.id} lg={4} md={6} className="mb-4">
                <motion.div
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  {renderCourseCard(course)}
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </motion.div>

      {/* Course Filters */}
      <Container className="mb-4">
        <Card className="shadow-sm">
          <Card.Body>
            <Row className="align-items-center">
              <Col md={3} className="mb-3 mb-md-0">
                <div className="d-flex align-items-center">
                  <Filter size={18} className="text-primary me-2" />
                  <h5 className="mb-0">Filter Courses</h5>
                </div>
              </Col>
              <Col md={3} className="mb-3 mb-md-0">
                <select 
                  className="form-select"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="foundational">Foundational</option>
                  <option value="functional">Functional Modules</option>
                  <option value="technical">Technical Modules</option>
                  <option value="specialized">Specialized / Hot in Industry</option>
                  <option value="careerPacks">Career Packs</option>
                </select>
              </Col>
              <Col md={3} className="mb-3 mb-md-0">
                <select 
                  className="form-select"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                >
                  <option value="all">All Durations</option>
                  <option value="3">3 Months</option>
                  <option value="6">6 Months</option>
                  <option value="9">9 Months</option>
                </select>
              </Col>
              <Col md={3}>
                <div className="input-group">
                  <input 
                    type="text" 
                    className="form-control"
                    placeholder="Search courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="outline-primary">
                    Search
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>

      {/* Main Courses Tabs */}
      <Container>
        <Tabs defaultActiveKey="all" className="mb-4">
          <Tab eventKey="all" title="All Courses">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Row>
                {filteredCourses().map(course => (
                  <Col key={course.id} lg={4} md={6} className="mb-4">
                    {renderCourseCard(course)}
                  </Col>
                ))}
              </Row>
            </motion.div>
          </Tab>
          <Tab eventKey="3months" title="3-Month Programs">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Row>
                {filteredCourses().filter(course => course.duration === 3).map(course => (
                  <Col key={course.id} lg={4} md={6} className="mb-4">
                    {renderCourseCard(course)}
                  </Col>
                ))}
              </Row>
            </motion.div>
          </Tab>
          <Tab eventKey="6months" title="6-Month Programs">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Row>
                {filteredCourses().filter(course => course.duration === 6).map(course => (
                  <Col key={course.id} lg={4} md={6} className="mb-4">
                    {renderCourseCard(course)}
                  </Col>
                ))}
              </Row>
            </motion.div>
          </Tab>
          <Tab eventKey="9months" title="9-Month Programs">
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Row>
                {filteredCourses().filter(course => course.duration === 9).map(course => (
                  <Col key={course.id} lg={4} md={6} className="mb-4">
                    {renderCourseCard(course)}
                  </Col>
                ))}
              </Row>
            </motion.div>
          </Tab>
        </Tabs>
      </Container>

      {/* Program Roadmap */}
      <Container className="my-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="shadow border-0 mb-5">
            <Card.Header className="bg-success ">
              <h3 className="mb-0 text-light text-center fw-bold">SAP Training Program Roadmap</h3>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4} className="border-end">
                  <div className="text-center mb-3">
                    <div className="bg-light p-3 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                      <Calendar size={36} className="text-primary" />
                    </div>
                    <h4>3-Month Programs</h4>
                    <p className="text-muted">Foundation & Quick Skills</p>
                  </div>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Week 1-4:</strong> SAP Fundamentals & Navigation
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Week 5-8:</strong> Core Business Processes & Module Introduction
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Week 9-12:</strong> Hands-on Practice & Project Work
                      </div>
                    </li>
                  </ul>
                  <div className="text-center">
                    <Badge bg="info" className="p-2">Perfect for Beginners</Badge>
                  </div>
                </Col>
                
                <Col md={4} className="border-end">
                  <div className="text-center mb-3">
                    <div className="bg-light p-3 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                      <Laptop size={36} className="text-primary" />
                    </div>
                    <h4>6-Month Programs</h4>
                    <p className="text-muted">Specialized & Certification</p>
                  </div>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 1:</strong> SAP Fundamentals & Module Introduction
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 2-3:</strong> Deep Module-Specific Training
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 4-5:</strong> Advanced Features & Integration
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 6:</strong> Certification Preparation & Project
                      </div>
                    </li>
                  </ul>
                  <div className="text-center">
                    <Badge bg="primary" className="p-2">Most Popular Choice</Badge>
                  </div>
                </Col>
                
                <Col md={4}>
                  <div className="text-center mb-3">
                    <div className="bg-light p-3 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: '80px', height: '80px' }}>
                      <Award size={36} className="text-primary" />
                    </div>
                    <h4>9-Month Programs</h4>
                    <p className="text-muted">Career Path & Advanced</p>
                  </div>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 1-2:</strong> SAP Fundamentals & Architecture
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 3-5:</strong> Primary Module Specialization
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 6-7:</strong> Secondary Module & Integration
                      </div>
                    </li>
                    <li className="mb-3 d-flex">
                      <Check size={20} className="text-success me-2 mt-1 flex-shrink-0" />
                      <div>
                        <strong>Month 8-9:</strong> Advanced Implementation & Capstone Project
                      </div>
                    </li>
                  </ul>
                  <div className="text-center">
                    <Badge bg="warning" text="dark" className="p-2">Career Transformer</Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>

      {/* Training Approach */}
      <Container className="mb-5">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <h2 className="text-center mb-4 fw-bold">Our SAP Training Approach</h2>
          <Row>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body>
                  <div className="mb-3">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                      <BookOpen size={30} className="text-light " />
                    </div>
                  </div>
                  <h5> Learn</h5>
                  <p className="small text-muted">Expert-led instruction with comprehensive course materials</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body>
                  <div className="mb-3">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                      <Code size={30} className="text-light" />
                    </div>
                  </div>
                  <h5>Practice</h5>
                  <p className="small text-muted">Hands-on labs with real SAP systems and environments</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body>
                  <div className="mb-3">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                      <Users size={30} className="text-light" />
                    </div>
                  </div>
                  <h5>Collaborate</h5>
                  <p className="small text-muted">Group projects mirroring real-world implementation scenarios</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="h-100 shadow-sm border-0 text-center">
                <Card.Body>
                  <div className="mb-3">
                    <div className="bg-primary bg-opacity-10 p-3 rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: '70px', height: '70px' }}>
                      <Award size={30} className="text-light" />
                    </div>
                  </div>
                  <h5>Certify</h5>
                  <p className="small text-muted">Industry-recognized certification preparation and guidance</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </motion.div>
      </Container>

     
  <Container fluid className="py-1 bg-light">
      <Row className="mb-5 text-center">
        <Col>
          <h2 className="display-5 fw-bold text-primary">
            🚀 SAP Functional & Technical Programs
          </h2>
          <p className="text-muted">
            Industry-ready, real-time curriculum for 3, 6, and 9 months duration with
            tools, projects, and future-focused skills.
          </p>
        </Col>
      </Row>

      {/* Functional Programs */}
      <Card className="mb-5 shadow-sm border-0 bg-white rounded-4">
        <Card.Body>
          <Card.Title className="fs-3 fw-semibold text-success">
            <FaUserTie className="me-2" /> SAP Functional Programs
          </Card.Title>
          <Table responsive bordered hover className="mt-4">
            <thead className="table-success">
              <tr>
                <th>Program Name</th>
                <th>Duration</th>
                <th>Modules Covered</th>
                <th>Tools & Platforms</th>
                <th>Project/Internship</th>
                <th>Ideal For</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>SAP FICO</td>
                <td>6 Months</td>
                <td>GL, AP/AR, Asset Mgmt, Controlling, Taxation</td>
                <td>SAP GUI, S/4HANA, Excel</td>
                <td>Finance cycle implementation</td>
                <td>Finance, Accounting, MBA</td>
              </tr>
              <tr>
                <td>SAP SD</td>
                <td>6 Months</td>
                <td>Sales Cycle, Pricing, Delivery, Billing</td>
                <td>SAP GUI, S/4HANA, Pricing Tool</td>
                <td>O2C Real-time Process</td>
                <td>Sales, Marketing, MBA</td>
              </tr>
              <tr>
                <td>SAP MM</td>
                <td>6 Months</td>
                <td>Procurement, Inventory, Valuation</td>
                <td>SAP GUI, S/4HANA</td>
                <td>Procurement Lifecycle Project</td>
                <td>SCM, Logistics</td>
              </tr>
              <tr>
                <td>SAP HCM</td>
                <td>6 Months</td>
                <td>Org Mgmt, Time Mgmt, Payroll</td>
                <td>SAP HCM, ESS/MSS Portal</td>
                <td>HR Implementation Project</td>
                <td>HR, MBA (HR)</td>
              </tr>
              <tr>
                <td>SAP PP</td>
                <td>6 Months</td>
                <td>BOM, Routing, MRP, Scheduling</td>
                <td>SAP GUI, MRP Tools</td>
                <td>Production Planning Implementation</td>
                <td>Manufacturing, Industrial Engg</td>
              </tr>
              <tr>
                <td>SAP SuccessFactors</td>
                <td>6 Months</td>
                <td>EC, RCM, PMGM, LMS</td>
                <td>SAP SuccessFactors Cloud</td>
                <td>Cloud HR Suite Project</td>
                <td>HR Professionals, Freshers</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Technical Programs */}
      <Card className="mb-3 shadow-sm border-0 bg-white rounded-4">
        <Card.Body>
          <Card.Title className="fs-3 fw-semibold text-danger">
            <FaLaptopCode className="me-2" /> SAP Technical Programs
          </Card.Title>
          <Table responsive bordered hover className="mt-4">
            <thead className="table-danger">
              <tr>
                <th>Program Name</th>
                <th>Duration</th>
                <th>Modules Covered</th>
                <th>Tools & Platforms</th>
                <th>Project/Internship</th>
                <th>Ideal For</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>SAP ABAP Developer</td>
                <td>6 Months</td>
                <td>Reports, Smartforms, BAPI, CDS, AMDP</td>
                <td>HANA Studio, Eclipse ADT, Git</td>
                <td>ABAP + CDS Integration</td>
                <td>B.Tech, MCA, Developers</td>
              </tr>
              <tr>
                <td>SAP UI5/Fiori</td>
                <td>6 Months</td>
                <td>HTML, JS, Fiori Elements, Smart Controls</td>
                <td>SAP Web IDE, Postman</td>
                <td>Fiori App for Sales/Inventory</td>
                <td>Web Devs, Frontend Devs</td>
              </tr>
              <tr>
                <td>SAP BASIS + HANA Admin</td>
                <td>6 Months</td>
                <td>Installation, Transport Mgmt, Security</td>
                <td>NetWeaver, MMC, Linux OS</td>
                <td>BASIS Setup Project</td>
                <td>Infra Engineers, SysAdmins</td>
              </tr>
              <tr>
                <td>SAP PI/PO Integration</td>
                <td>6 Months</td>
                <td>ESR, SLD, ID, Mapping, Adapters</td>
                <td>SAP PO, SOAPUI</td>
                <td>Enterprise System Integration</td>
                <td>Middleware Devs, Java Devs</td>
              </tr>
              <tr>
                <td>SAP BTP Full-Stack Dev</td>
                <td>6 Months</td>
                <td>CAP, RAP, CDS, MTA, Cloud Foundry</td>
                <td>SAP BTP, GitHub, HANA Cloud</td>
                <td>Cloud-based Fiori App Deployment</td>
                <td>Full-stack Devs, SAP Architects</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Row className="text-center mb-5"> 
        <Col>
          <p className="text-muted fw-bolder">
            ✨ Programs are offered with 3, 6, and 9-month tracks. Live projects, industry tools, and real-world problem-solving make this ideal for job-seekers and professionals.
          </p>
        </Col>
      </Row>
    </Container>

 {/* Call to Action */}
      <Container className="mb-0">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="bg-success text-white shadow border-0">
            <Card.Body className="p-5 text-center">
              <h2 className="mb-3 fw-bold">Ready to Transform Your SAP Career?</h2>
              <p className="lead mb-4">Join thousands of successful SAP professionals who started their journey with us</p>
              <Button variant="light" size="lg" className="fw-bold px-4 py-2 me-3 my-2 ms-2 shadow-sm"
              href="/enroll"
              >
              Enroll Course
              </Button>
              <Button variant="dark" size="lg" className="fw-bold px-4 py-2 me-3 my-2 ms-2 shadow-sm"
              href="/admission"
              >
                Get Admission
              </Button>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>

    </Container>
  );
}